import {
  CountryAlpha2,
  DocumentReviewType,
  TaxNumberType
} from 'generated/graphql'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { validate as validatePostalCode } from 'postal-codes-js'
import { z } from 'zod'

export const snakeCaseRegex = /^[a-z0-9_]+$/

export const stringifiedNumberSchema = (
  validateSchema: z.ZodNumber,
  options?: { allowEmpty?: boolean }
) => {
  const stringToNumber = z
    .string()
    .min(1)
    .transform((val, ctx) => {
      const parsed = Number(val)
      if (Number.isNaN(parsed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Not a valid number'
        })
        return z.NEVER
      }
      return parsed
    })
    .pipe(validateSchema)

  const schema = options?.allowEmpty
    ? z.union([z.literal(''), stringToNumber])
    : stringToNumber

  return schema.transform(val =>
    typeof val === 'number' ? val.toString() : val
  )
}

export const urlSchema = (options?: { allowEmpty?: boolean }) => {
  const validUrlSchema = z
    .string()
    .transform(val => {
      if (!/^https?:\/\//i.test(val)) {
        return `https://${val}`
      }
      return val
    })
    .pipe(
      z
        .string()
        .url('Please enter a valid URL')
        .refine(val => val.includes('.'), {
          message: 'Please enter a valid URL with a domain (e.g. example.com)'
        })
    )

  const schema = options?.allowEmpty
    ? z.union([z.literal(''), validUrlSchema])
    : validUrlSchema

  return schema
}

export const addressSchema = z.preprocess(
  (val: unknown) => {
    const data = val as Record<string, unknown> | undefined
    const country = data?.countryAlpha2
    if (
      (country === CountryAlpha2.Us || country === CountryAlpha2.Ca) &&
      !(data?.state as string | null | undefined)?.trim()
    ) {
      return { ...data, state: NaN }
    }
    return val
  },
  z
    .object({
      addressLine: z.string().trim().min(1),
      addressLine2: z.string().trim().nullish(),
      city: z.string().trim().min(1),
      countryAlpha2: z.nativeEnum(CountryAlpha2).nullish(),
      state: z.string({ invalid_type_error: 'Required' }).trim().nullish(),
      zip: z.string().trim().min(1)
    })
    .refine(
      data => {
        if (!data.zip || !data.countryAlpha2) return true
        return validatePostalCode(data.countryAlpha2, data.zip) === true
      },
      {
        message: 'invalidPostalCode',
        path: ['zip']
      }
    )
)

export const optionalAddressSchema = z.object({
  addressLine: z.string({ invalid_type_error: 'Required' }).trim().nullish(),
  addressLine2: z.string().trim().nullish(),
  city: z.string({ invalid_type_error: 'Required' }).trim().nullish(),
  countryAlpha2: z.nativeEnum(CountryAlpha2).nullish(),
  state: z.string({ invalid_type_error: 'Required' }).trim().nullish(),
  zip: z.string({ invalid_type_error: 'Required' }).trim().nullish()
})

export const phoneNumberSchema = (options: {
  allowEmpty?: boolean
  message: string
}) => {
  const validPhoneNumber = z
    .string()
    .min(1)
    .transform(val => val.trim())
    .refine(phone => isValidPhoneNumber(phone), {
      message: options.message
    })

  const schema = options.allowEmpty
    ? z.union([z.literal(''), validPhoneNumber]).nullish()
    : validPhoneNumber

  return schema
}

export const dateSchema = (validationSchema: z.ZodDate) =>
  z
    .number()
    .transform(val => new Date(val))
    .pipe(validationSchema)
    .refine(val => val <= new Date(), { message: 'dateInFuture' })
    .transform(val => val.getTime())

export const documentSchema = z.object({
  id: z.string(),
  type: z.string().nullish(),
  status: z.nativeEnum(DocumentReviewType).nullish(),
  url: z.string().nullish(),
  path: z.string().nullish()
})

export const socialSecurityNumberSchema = (options?: {
  allowEmpty?: boolean
}) => {
  const validSsn = z
    .string()
    .min(1)
    .regex(/^\d{3}-\d{2}-\d{4}$/, { message: 'invalidSsn' })

  const schema = options?.allowEmpty
    ? z.union([z.literal(''), validSsn])
    : validSsn

  return schema
}

export const employerIdentificationNumberSchema = (options?: {
  allowEmpty?: boolean
}) => {
  const validEin = z
    .string()
    .min(1)
    .regex(/^\d{2}-\d{7}$/, { message: 'invalidEin' })

  const schema = options?.allowEmpty
    ? z.union([z.literal(''), validEin])
    : validEin

  return schema
}

export const individualTaxpayerIdentificationNumberSchema = (options?: {
  allowEmpty?: boolean
}) => {
  const validItin = z
    .string()
    .min(1)
    .regex(/^9\d{2}-\d{2}-\d{4}$/, { message: 'invalidItin' })

  const schema = options?.allowEmpty
    ? z.union([z.literal(''), validItin])
    : validItin

  return schema
}

export const getTaxIdValidationResult = (
  type: TaxNumberType | null | undefined,
  value: string | null | undefined
): { success: boolean; message?: string } => {
  if (!type || !value) return { success: true }

  let result
  switch (type) {
    case TaxNumberType.Ein:
      result = employerIdentificationNumberSchema().safeParse(value)
      break
    case TaxNumberType.Ssn:
      result = socialSecurityNumberSchema().safeParse(value)
      break
    case TaxNumberType.Itin:
      result = individualTaxpayerIdentificationNumberSchema().safeParse(value)
      break
    default:
      return { success: true }
  }

  if (!result.success) {
    return {
      success: false,
      message: result.error.errors[0]?.message || 'Invalid format'
    }
  }

  return { success: true }
}
